import React from "react";
import { Trophy, RotateCcw, Home, ArrowRight, Skull, Play, Target, Dumbbell, Star } from "lucide-react";
import { FingerKeyboard, FingerLegend } from "@/components/FingerKeyboard";
import { PixelHands } from "@/components/PixelHands";
import { STAR_THRESHOLDS } from "@/game/gameData";

const starsHint = (stars) => {
  if (stars === 3) return "Perfect accuracy — three stars!";
  if (stars === 2) return `Great! Reach ${STAR_THRESHOLDS[2]}% accuracy for 3 stars.`;
  if (stars === 1) return `Cleared! Reach ${STAR_THRESHOLDS[1]}% accuracy for 2 stars.`;
  return `Cleared, but too many mistakes for a star. Reach ${STAR_THRESHOLDS[0]}% accuracy to earn one.`;
};

export function StarRow({ stars = 0, size = 16, animate = false, testid }) {
  return (
    <div className="flex items-center gap-1" data-testid={testid} data-stars={stars}>
      {[0, 1, 2].map((i) => (
        <Star
          key={i}
          size={size}
          className={`${i < stars ? "text-[#ffe600] fill-[#ffe600]" : "text-[#3a366b]"} ${animate && i < stars ? "tb-star-pop" : ""}`}
          style={animate ? { animationDelay: `${i * 0.2}s`, filter: i < stars ? "drop-shadow(0 0 6px #ffe600)" : "none" } : undefined}
        />
      ))}
    </div>
  );
}

export function LevelIntro({ level, onStart, practice = false }) {
  return (
    <Overlay>
      <div
        className={`tb-slidedown flex flex-col items-center gap-3 bg-[#121124] border-4 ${practice ? "border-[#ffb000] shadow-[0_0_40px_rgba(255,176,0,0.35)]" : "border-[#00f0ff] shadow-[0_0_40px_rgba(0,240,255,0.35)]"} rounded-2xl p-5 sm:p-7 max-w-lg w-full`}
        data-testid="level-intro"
      >
        <div className="font-arcade text-[10px] text-[#ff007f] tracking-widest">
          {practice && <span className="text-[#ffb000] mr-2" data-testid="practice-badge">PRACTICE ·</span>}
          LEVEL {level.id}
        </div>
        <h2 className="font-arcade text-base sm:text-lg text-cyan-300 text-glow-cyan tracking-wider text-center">
          {level.name}
        </h2>
        <div className="flex items-start gap-2 text-center justify-center">
          <Target size={18} className="text-[#ffe600] mt-0.5 shrink-0" />
          <p className="font-vt text-xl text-[#ffe600]" data-testid="level-focus">
            {level.focus}
          </p>
        </div>
        <p className="font-vt text-lg text-slate-300 text-center leading-snug">
          {level.intro.trim()}
        </p>
        {level.id > 1 && (
          <p className="font-vt text-base text-center" data-testid="rainbow-note">
            <span className="tb-rainbow-text font-bold">Rainbow</span>{" "}
            <span className="text-slate-300">aliens never cost a life!</span>
          </p>
        )}
        {practice && (
          <p className="font-vt text-base text-[#ffb000] text-center" data-testid="practice-note">
            No lives, no game over. Aliens keep coming until you press FINISH or ESC.
          </p>
        )}
        <PixelHands nextChar={null} size={52} restKeys={level.guideKeys || level.newKeys}>
          <FingerKeyboard highlightKeys={level.guideKeys || level.newKeys} outlineKeys={level.outlineKeys || []} compact />
        </PixelHands>
        <FingerLegend />
        <ArcadeBtn testid="start-level-btn" onClick={onStart} color="#39ff14" icon={<Play size={16} />}>
          Start · press SPACE
        </ArcadeBtn>
        <p className="font-vt text-lg text-[#ffe600] text-center" data-testid="home-row-reminder">
          Feel the bumps, and always return to home row.
        </p>
      </div>
    </Overlay>
  );
}

export function LevelComplete({ result, hasNext, onNext, onReplay, onHome }) {
  return (
    <Overlay>
      <div className="tb-slidedown flex flex-col items-center gap-5 bg-[#151230] border-4 border-[#ffb000] rounded-2xl p-6 sm:p-8 shadow-[0_0_40px_rgba(255,176,0,0.4)] max-w-sm w-full">
        <Trophy size={44} className="text-[#ffb000]" />
        <h2 className="font-arcade text-lg sm:text-xl text-[#ffb000] text-glow-cyan tracking-wider text-center">
          LEVEL CLEAR!
        </h2>
        <StarRow stars={result.stars} size={40} animate testid="result-stars" />
        <p className="font-vt text-lg text-center text-slate-300 -mt-2" data-testid="stars-hint">
          {starsHint(result.stars)}
        </p>
        <div className="grid grid-cols-3 gap-3 w-full text-center">
          <StatBox label="SCORE" value={result.score} testid="result-score" />
          <StatBox label="WPM" value={result.wpm} />
          <StatBox label="ACCURACY" value={`${result.accuracy}%`} />
        </div>
        <div className="flex flex-col gap-2 w-full mt-2">
          {hasNext && (
            <ArcadeBtn
              testid="next-level-btn"
              onClick={onNext}
              color="#39ff14"
              icon={<ArrowRight size={16} />}
            >
              Next Level
            </ArcadeBtn>
          )}
          <ArcadeBtn
            testid="replay-btn"
            onClick={onReplay}
            color="#00f0ff"
            icon={<RotateCcw size={16} />}
          >
            Play Again
          </ArcadeBtn>
          <ArcadeBtn
            testid="home-btn"
            onClick={onHome}
            color="#ff007f"
            icon={<Home size={16} />}
          >
            Main Menu
          </ArcadeBtn>
        </div>
      </div>
    </Overlay>
  );
}

export function PracticeSummary({ result, onReplay, onHome }) {
  return (
    <Overlay>
      <div
        className="tb-slidedown flex flex-col items-center gap-5 bg-[#1a1608] border-4 border-[#ffb000] rounded-2xl p-6 sm:p-8 shadow-[0_0_40px_rgba(255,176,0,0.4)] max-w-sm w-full"
        data-testid="practice-summary"
      >
        <Dumbbell size={44} className="text-[#ffb000]" />
        <h2 className="font-arcade text-lg sm:text-xl text-[#ffb000] tracking-wider text-center">
          NICE PRACTICE!
        </h2>
        <div className="grid grid-cols-2 gap-3 w-full text-center">
          <StatBox label="HIT" value={result.destroyed} testid="result-hit" />
          <StatBox label="MISSED" value={result.escaped} testid="result-missed" />
          <StatBox label="WPM" value={result.wpm} />
          <StatBox label="ACCURACY" value={`${result.accuracy}%`} />
        </div>
        <div className="flex flex-col gap-2 w-full mt-2">
          <ArcadeBtn testid="replay-btn" onClick={onReplay} color="#39ff14" icon={<RotateCcw size={16} />}>
            Practice Again
          </ArcadeBtn>
          <ArcadeBtn testid="home-btn" onClick={onHome} color="#ff007f" icon={<Home size={16} />}>
            Main Menu
          </ArcadeBtn>
        </div>
      </div>
    </Overlay>
  );
}

export function GameOver({ result, onRetry, onHome }) {
  return (
    <Overlay>
      <div className="tb-slidedown flex flex-col items-center gap-5 bg-[#221024] border-4 border-[#ff2a2a] rounded-2xl p-6 sm:p-8 shadow-[0_0_40px_rgba(255,42,42,0.4)] max-w-sm w-full">
        <Skull size={44} className="text-[#ff2a2a]" />
        <h2 className="font-arcade text-lg sm:text-xl text-[#ff2a2a] text-glow-pink tracking-wider text-center">
          GAME OVER
        </h2>
        <div className="grid grid-cols-2 gap-3 w-full text-center">
          <StatBox label="SCORE" value={result.score} testid="result-score" />
          <StatBox label="ACCURACY" value={`${result.accuracy}%`} />
        </div>
        <div className="flex flex-col gap-2 w-full mt-2">
          <ArcadeBtn
            testid="retry-btn"
            onClick={onRetry}
            color="#39ff14"
            icon={<RotateCcw size={16} />}
          >
            Try Again
          </ArcadeBtn>
          <ArcadeBtn
            testid="home-btn"
            onClick={onHome}
            color="#ff007f"
            icon={<Home size={16} />}
          >
            Main Menu
          </ArcadeBtn>
        </div>
      </div>
    </Overlay>
  );
}

function Overlay({ children }) {
  return (
    <div
      data-testid="game-overlay"
      className="absolute inset-0 z-50 flex items-center justify-center bg-[#06060e]/85 backdrop-blur-sm px-4"
    >
      {children}
    </div>
  );
}

function StatBox({ label, value, testid }) {
  return (
    <div className="bg-[#0d0d1a] border border-[#3a366b] rounded-lg py-2">
      <div className="font-arcade text-[8px] text-slate-400 tracking-widest">
        {label}
      </div>
      <div
        data-testid={testid}
        className="font-vt text-2xl text-[#39ff14] text-glow-green"
      >
        {value}
      </div>
    </div>
  );
}

function ArcadeBtn({ children, onClick, color, icon, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{ borderColor: color, color }}
      className="w-full flex items-center justify-center gap-2 bg-[#1c1a36] border-2 hover:bg-white/5 active:translate-y-0.5 transition-all uppercase tracking-widest font-arcade text-[10px] py-3 rounded-lg"
    >
      {icon} {children}
    </button>
  );
}
